import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2, Wallet } from "lucide-react";

import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AppEmptyState from "@/components/ui/AppEmptyState";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getApiErrorMessage } from "@/lib/api";
import {
  FIXED_COST_CATEGORIES_ORDER,
  getCategoryLabel,
} from "@/lib/fixedCostCategories";
import { TESTIDS } from "@/lib/testids";
import {
  createFixedCost,
  deleteFixedCost,
  listFixedCosts,
  updateFixedCost,
} from "@/lib/pilotageApi";
import {
  formatMonth,
  toAnnualAmount,
  toMonthlyAmount,
} from "@/lib/businessRules";
import { formatEUR0 } from "@/lib/pilotageUi";

const FILTERS = [
  { key: "ALL", label: "Toutes" },
  { key: "ACTIVE", label: "Actives" },
  { key: "INACTIVE", label: "Inactives" },
];

const SORT_OPTIONS = [
  { key: "CATEGORY_NAME", label: "Par catégorie + Nom" },
  { key: "AMOUNT_DESC", label: "Montant (DESC)" },
];

const AMOUNT_MODES = [
  { key: "MONTHLY", label: "Mensuel" },
  { key: "ANNUAL", label: "Annuel" },
];
const PERIODICITY_OPTIONS = [
  { key: "RECURRENTE", label: "Récurrente" },
  { key: "PONCTUELLE", label: "Ponctuelle" },
];
const FIXED_COSTS_FILTER_STORAGE_KEY = "pilotage_fixed_costs_filter";
const FIXED_COSTS_SORT_STORAGE_KEY = "pilotage_fixed_costs_sort";

const EMPTY_FORM = {
  name: "",
  category: FIXED_COST_CATEGORIES_ORDER[0],
  category_detail: "",
  amount: "",
  is_active: true,
  periodicity: "RECURRENTE",
  imputation_month: "",
  start_month: "",
  end_month: "",
  notes: "",
};

const CATEGORY_ORDER_INDEX = FIXED_COST_CATEGORIES_ORDER.reduce((accumulator, category, index) => {
  accumulator[category] = index;
  return accumulator;
}, {});

const toAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const readPreference = (storageKey, fallback = "") => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const value = window.localStorage.getItem(storageKey);
    return typeof value === "string" ? value : fallback;
  } catch {
    return fallback;
  }
};

const writePreference = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    // Ignore storage errors to keep the UI functional.
  }
};

const resolveInitialFilterMode = () => {
  const candidate = readPreference(FIXED_COSTS_FILTER_STORAGE_KEY, "ALL");
  return FILTERS.some((filter) => filter.key === candidate) ? candidate : "ALL";
};

const resolveInitialSortMode = () => {
  const candidate = readPreference(FIXED_COSTS_SORT_STORAGE_KEY, "CATEGORY_NAME");
  return SORT_OPTIONS.some((option) => option.key === candidate) ? candidate : "CATEGORY_NAME";
};

const formatPeriod = (fixedCost) => {
  const startMonth = fixedCost.start_month;
  const endMonth = fixedCost.end_month;

  if (!startMonth && !endMonth) {
    return "Sans borne";
  }

  const startLabel = startMonth ? formatMonth(startMonth) : "Dès maintenant";
  const endLabel = endMonth ? formatMonth(endMonth) : "Sans fin";
  return `${startLabel} -> ${endLabel}`;
};

const formatImputationMonth = (monthKey) => {
  const formatted = formatMonth(monthKey);
  return formatted ? formatted.charAt(0).toUpperCase() + formatted.slice(1) : "";
};

const FixedCostsPage = () => {
  const [fixedCosts, setFixedCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFixedCost, setEditingFixedCost] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [amountMode, setAmountMode] = useState("MONTHLY");
  const [formError, setFormError] = useState("");
  const [filterMode, setFilterMode] = useState(resolveInitialFilterMode);
  const [sortMode, setSortMode] = useState(resolveInitialSortMode);

  const loadFixedCosts = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await listFixedCosts();
      setFixedCosts(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de charger les charges fixes."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadFixedCosts();
  }, []);
  useEffect(() => {
    writePreference(FIXED_COSTS_FILTER_STORAGE_KEY, filterMode);
  }, [filterMode]);
  useEffect(() => {
    writePreference(FIXED_COSTS_SORT_STORAGE_KEY, sortMode);
  }, [sortMode]);

  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const currentYearPrefix = `${new Date().getFullYear()}-`;

  const activeMonthlyGlobalTotal = useMemo(
    () =>
      fixedCosts.reduce((accumulator, fixedCost) => {
        if (!fixedCost.is_active) {
          return accumulator;
        }
        if (fixedCost.periodicity === "PONCTUELLE") {
          const imputation = fixedCost.imputation_month || fixedCost.start_month;
          return imputation === currentMonthKey
            ? accumulator + toAmount(fixedCost.amount_monthly)
            : accumulator;
        }
        return accumulator + toAmount(fixedCost.amount_monthly);
      }, 0),
    [fixedCosts, currentMonthKey],
  );
  const activeAnnualGlobalTotal = useMemo(
    () =>
      fixedCosts.reduce((accumulator, fixedCost) => {
        if (!fixedCost.is_active) {
          return accumulator;
        }
        if (fixedCost.periodicity === "PONCTUELLE") {
          const imputation = fixedCost.imputation_month || fixedCost.start_month;
          return imputation && imputation.startsWith(currentYearPrefix)
            ? accumulator + toAmount(fixedCost.amount_monthly)
            : accumulator;
        }
        return accumulator + toAmount(fixedCost.amount_monthly) * 12;
      }, 0),
    [fixedCosts, currentYearPrefix],
  );

  const filteredFixedCosts = useMemo(() => {
    if (filterMode === "ACTIVE") {
      return fixedCosts.filter((fixedCost) => fixedCost.is_active);
    }

    if (filterMode === "INACTIVE") {
      return fixedCosts.filter((fixedCost) => !fixedCost.is_active);
    }

    return fixedCosts;
  }, [fixedCosts, filterMode]);

  const sortedFixedCosts = useMemo(() => {
    const values = [...filteredFixedCosts];

    if (sortMode === "AMOUNT_DESC") {
      values.sort((left, right) => {
        const amountDiff = toAmount(right.amount_monthly) - toAmount(left.amount_monthly);
        if (amountDiff !== 0) {
          return amountDiff;
        }

        return String(left.name || "").localeCompare(String(right.name || ""), "fr-FR");
      });

      return values;
    }

    values.sort((left, right) => {
      const categoryDiff =
        (CATEGORY_ORDER_INDEX[left.category] ?? 10_000) -
        (CATEGORY_ORDER_INDEX[right.category] ?? 10_000);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }

      return String(left.name || "").localeCompare(String(right.name || ""), "fr-FR");
    });

    return values;
  }, [filteredFixedCosts, sortMode]);

  const groupedByCategory = useMemo(() => {
    const map = new Map();

    sortedFixedCosts.forEach((fixedCost) => {
      const category = fixedCost.category;
      if (!map.has(category)) {
        map.set(category, []);
      }

      map.get(category).push(fixedCost);
    });

    return FIXED_COST_CATEGORIES_ORDER
      .filter((category) => map.has(category))
      .map((category) => {
        const rows = map.get(category);
        const totalMonthly = rows.reduce(
          (accumulator, fixedCost) => accumulator + toAmount(fixedCost.amount_monthly),
          0,
        );

        return {
          category,
          label: getCategoryLabel(category),
          totalMonthly,
          rows,
        };
      });
  }, [sortedFixedCosts]);

  const openCreateModal = () => {
    setEditingFixedCost(null);
    setFormValues({
      ...EMPTY_FORM,
      category: FIXED_COST_CATEGORIES_ORDER[0],
    });
    setAmountMode("MONTHLY");
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (fixedCost) => {
    setEditingFixedCost(fixedCost);
    const isPonctuelle = fixedCost.periodicity === "PONCTUELLE";
    setFormValues({
      name: fixedCost.name || "",
      category: fixedCost.category || FIXED_COST_CATEGORIES_ORDER[0],
      category_detail: fixedCost.category_detail || "",
      amount: String(fixedCost.amount_monthly ?? ""),
      is_active: Boolean(fixedCost.is_active),
      periodicity: isPonctuelle ? "PONCTUELLE" : "RECURRENTE",
      imputation_month: fixedCost.imputation_month || (isPonctuelle ? fixedCost.start_month : "") || "",
      start_month: fixedCost.start_month || "",
      end_month: fixedCost.end_month || "",
      notes: fixedCost.notes || "",
    });
    setAmountMode("MONTHLY");
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);

    if (!nextOpen) {
      setEditingFixedCost(null);
      setFormValues(EMPTY_FORM);
      setAmountMode("MONTHLY");
      setFormError("");
    }
  };

  const handleAmountModeChange = (nextMode) => {
    if (nextMode === amountMode) {
      return;
    }

    setFormValues((current) => {
      const currentAmount = Number(current.amount);
      if (!Number.isFinite(currentAmount)) {
        return current;
      }

      if (amountMode === "MONTHLY" && nextMode === "ANNUAL") {
        return { ...current, amount: String(toAnnualAmount(currentAmount, "MONTHLY")) };
      }

      if (amountMode === "ANNUAL" && nextMode === "MONTHLY") {
        return { ...current, amount: String(toMonthlyAmount(currentAmount, "ANNUAL")) };
      }

      return current;
    });

    setAmountMode(nextMode);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFormError("");

    const trimmedName = String(formValues.name || "").trim();
    const rawAmount = Number(formValues.amount);
    const categoryIsAutres = formValues.category === "AUTRES";
    const categoryDetail = String(formValues.category_detail || "").trim();
    const isPonctuelle = formValues.periodicity === "PONCTUELLE";
    const imputationMonth = String(formValues.imputation_month || "").trim();
    const startMonth = String(formValues.start_month || "").trim();
    const endMonth = String(formValues.end_month || "").trim();
    const amountMonthly = isPonctuelle ? rawAmount : toMonthlyAmount(rawAmount, amountMode);

    if (!trimmedName) {
      setSaving(false);
      setFormError("Le nom de la charge est requis.");
      return;
    }

    if (!Number.isFinite(rawAmount) || rawAmount < 0) {
      setSaving(false);
      setFormError(
        isPonctuelle
          ? "Le montant doit être un nombre supérieur ou égal à 0."
          : (amountMode === "ANNUAL"
            ? "Le montant annuel doit être un nombre supérieur ou égal à 0."
            : "Le montant mensuel doit être un nombre supérieur ou égal à 0.")
      );
      return;
    }

    if (categoryIsAutres && !categoryDetail) {
      setSaving(false);
      setFormError("Le détail catégorie est requis pour la catégorie Autres.");
      return;
    }

    if (isPonctuelle && !imputationMonth) {
      setSaving(false);
      setFormError("Le mois d'imputation est requis pour une charge ponctuelle.");
      return;
    }

    if (!isPonctuelle && startMonth && endMonth && startMonth > endMonth) {
      setSaving(false);
      setFormError("La période est invalide: start_month doit être <= end_month.");
      return;
    }

    const payload = {
      name: trimmedName,
      category: formValues.category,
      category_detail: categoryIsAutres ? categoryDetail : null,
      amount_monthly: amountMonthly,
      is_active: Boolean(formValues.is_active),
      periodicity: formValues.periodicity,
      imputation_month: isPonctuelle ? imputationMonth : null,
      start_month: isPonctuelle ? imputationMonth : (startMonth || null),
      end_month: isPonctuelle ? imputationMonth : (endMonth || null),
      notes: String(formValues.notes || "").trim() || null,
    };

    try {
      if (editingFixedCost) {
        await updateFixedCost(editingFixedCost.id, payload);
      } else {
        await createFixedCost(payload);
      }

      closeModal(false);
      await loadFixedCosts();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible d'enregistrer la charge fixe."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fixedCost) => {
    const shouldDelete = window.confirm(`Supprimer la charge fixe "${fixedCost.name}" ?`);
    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteFixedCost(fixedCost.id);
      await loadFixedCosts();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de supprimer la charge fixe."));
    }
  };

  const showEmptyState = !loading && fixedCosts.length === 0;
  const showFilteredEmptyState = !loading && fixedCosts.length > 0 && groupedByCategory.length === 0;
  const openCategoryValues = groupedByCategory.map((group) => group.category);
  const categoryDetailVisible = formValues.category === "AUTRES";

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Charges fixes
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Suivi des charges structure (OPEX) avec regroupement par catégorie.
            </p>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3 lg:w-auto">
            <div className="grid min-w-0 w-full gap-3 md:grid-cols-2">
              <div
                className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                data-testid={TESTIDS.pilotage.fixedCosts.totalMonthly}
              >
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Total mensuel (actives)
                </p>
                <p className="mt-1 text-xl font-semibold text-white">
                  {formatEUR0(activeMonthlyGlobalTotal)}
                </p>
              </div>
              <div
                className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                data-testid={TESTIDS.pilotage.fixedCosts.totalAnnual}
              >
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Total annuel (estimé)
                </p>
                <p className="mt-1 text-xl font-semibold text-white">
                  {formatEUR0(activeAnnualGlobalTotal)}
                </p>
                <p className="mt-1 text-xs text-slate-500">(Total mensuel x 12)</p>
              </div>
            </div>
            <Button type="button" onClick={openCreateModal} className="btn-primary">
              <Plus className="h-4 w-4" />
              Ajouter une charge
            </Button>
          </div>
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {loading ? (
        <AppLoadingState
          title="Chargement des charges fixes"
          description="Récupération des données pilotage en cours."
          testId="fixed-costs-loading-state"
        />
      ) : null}

      {showEmptyState ? (
        <AppEmptyState
          icon={Wallet}
          title="Aucune charge fixe"
          description="Ajoutez votre première charge pour intégrer les OPEX de structure au pilotage."
          actionLabel="Ajouter une charge"
          onAction={openCreateModal}
          testId="fixed-costs-empty-state"
        />
      ) : null}

      {!loading && !showEmptyState ? (
        <>
          <section className="rounded-3xl border border-white/10 glass p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((filter) => {
                  const isSelected = filterMode === filter.key;
                  return (
                    <Button
                      key={filter.key}
                      type="button"
                      variant="outline"
                      onClick={() => setFilterMode(filter.key)}
                      className={
                        isSelected
                          ? "border-lenvia-blue/40 bg-lenvia-blue/10 text-lenvia-blue hover:bg-lenvia-blue/15"
                          : "border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
                      }
                    >
                      {filter.label}
                    </Button>
                  );
                })}
              </div>

              <div className="flex min-w-[220px] items-center gap-2">
                <label
                  htmlFor="fixed-costs-sort"
                  className="text-xs uppercase tracking-[0.18em] text-slate-500"
                >
                  Tri
                </label>
                <select
                  id="fixed-costs-sort"
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {showFilteredEmptyState ? (
            <AppEmptyState
              icon={Wallet}
              title="Aucun résultat"
              description="Aucune charge ne correspond aux filtres appliqués."
              testId="fixed-costs-filtered-empty-state"
            />
          ) : (
            <Accordion
              type="multiple"
              defaultValue={openCategoryValues}
              className="rounded-3xl border border-white/10 glass px-4 py-2"
            >
              {groupedByCategory.map((group) => (
                <AccordionItem key={group.category} value={group.category} className="border-white/10">
                  <AccordionTrigger className="gap-4 py-4 text-left hover:no-underline">
                    <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
                      <span className="text-base font-semibold text-white">{group.label}</span>
                      <span className="shrink-0 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">
                        Total catégorie: {formatEUR0(group.totalMonthly)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                      <table className="min-w-full divide-y divide-white/10">
                        <thead className="bg-white/[0.03]">
                          <tr>
                            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Nom
                            </th>
                            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Montant mensuel
                            </th>
                            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Période
                            </th>
                            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Actif
                            </th>
                            <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {group.rows.map((fixedCost) => (
                            <tr key={fixedCost.id} className="hover:bg-white/[0.02]">
                              <td className="min-w-0 px-4 py-3 align-top text-sm text-slate-100">
                                <p className="max-w-[280px] truncate font-medium text-white" title={fixedCost.name}>
                                  {fixedCost.name}
                                </p>
                                {fixedCost.category === "AUTRES" && fixedCost.category_detail ? (
                                  <p className="mt-1 text-xs text-slate-400" title={fixedCost.category_detail}>
                                    {fixedCost.category_detail}
                                  </p>
                                ) : null}
                              </td>
                              <td className="px-4 py-3 align-top text-sm text-slate-100">
                                <p>{formatEUR0(fixedCost.amount_monthly)}</p>
                                <p className="mt-1 text-xs text-slate-400">
                                  {fixedCost.periodicity === "PONCTUELLE"
                                    ? "Charge unique"
                                    : `Annuel: ${formatEUR0(toAmount(fixedCost.amount_monthly) * 12)}`}
                                </p>
                              </td>
                              <td className="px-4 py-3 align-top text-sm text-slate-300">
                                {fixedCost.periodicity === "PONCTUELLE" ? (
                                  <div className="flex flex-col gap-1 items-start">
                                    <span className="inline-flex rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-xs font-semibold text-violet-300">
                                      PONCTUELLE
                                    </span>
                                    <span className="text-xs text-slate-300 font-medium">
                                      {formatImputationMonth(fixedCost.imputation_month || fixedCost.start_month)}
                                    </span>
                                  </div>
                                ) : (
                                  formatPeriod(fixedCost)
                                )}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <span
                                  className={
                                    fixedCost.is_active
                                      ? "inline-flex rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-semibold text-emerald-300"
                                      : "inline-flex rounded-full border border-slate-500/30 bg-slate-500/10 px-2 py-1 text-xs font-semibold text-slate-300"
                                  }
                                >
                                  {fixedCost.is_active ? "Oui" : "Non"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right align-top">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditModal(fixedCost)}
                                    className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                    Modifier
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDelete(fixedCost)}
                                    className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Supprimer
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </>
      ) : null}

      <PilotageModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={editingFixedCost ? "Modifier une charge fixe" : "Ajouter une charge fixe"}
        description="Les montants sont stockés mensuellement et intégrés au bénéfice net du dashboard."
        onSubmit={handleSubmit}
        submitLabel={editingFixedCost ? "Enregistrer" : "Créer"}
        isSubmitting={saving}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <PilotageField label="Type de charge" htmlFor="fixed-cost-periodicity">
              <div className="flex gap-2">
                {PERIODICITY_OPTIONS.map((mode) => {
                  const isSelected = formValues.periodicity === mode.key;
                  return (
                    <Button
                      key={mode.key}
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setFormValues((current) => ({
                          ...current,
                          periodicity: mode.key,
                        }))
                      }
                      className={
                        isSelected
                          ? "border-lenvia-blue/40 bg-lenvia-blue/10 text-lenvia-blue hover:bg-lenvia-blue/15"
                          : "border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
                      }
                    >
                      {mode.label}
                    </Button>
                  );
                })}
              </div>
            </PilotageField>
          </div>

          <PilotageField label="Nom" htmlFor="fixed-cost-name">
            <Input
              id="fixed-cost-name"
              value={formValues.name}
              onChange={(event) =>
                setFormValues((current) => ({ ...current, name: event.target.value }))
              }
              placeholder="Salaire RH, CRM, Honoraires..."
              required
            />
          </PilotageField>

          <PilotageField label="Catégorie" htmlFor="fixed-cost-category">
            <select
              id="fixed-cost-category"
              value={formValues.category}
              onChange={(event) =>
                setFormValues((current) => ({ ...current, category: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              required
            >
              {FIXED_COST_CATEGORIES_ORDER.map((category) => (
                <option key={category} value={category}>
                  {getCategoryLabel(category)}
                </option>
              ))}
            </select>
          </PilotageField>

          {categoryDetailVisible ? (
            <PilotageField label="Détail catégorie" htmlFor="fixed-cost-category-detail">
              <Input
                id="fixed-cost-category-detail"
                value={formValues.category_detail}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    category_detail: event.target.value,
                  }))
                }
                placeholder="Frais divers, abonnement X..."
                required
              />
            </PilotageField>
          ) : null}

          {formValues.periodicity === "PONCTUELLE" ? (
            <PilotageField label="Mois d'imputation" htmlFor="fixed-cost-imputation-month">
              <Input
                id="fixed-cost-imputation-month"
                type="month"
                value={formValues.imputation_month}
                onChange={(event) =>
                  setFormValues((current) => ({ ...current, imputation_month: event.target.value }))
                }
                required
              />
            </PilotageField>
          ) : null}

          <PilotageField
            label="Montant"
            hint={formValues.periodicity === "PONCTUELLE" ? "Ponctuel" : (amountMode === "ANNUAL" ? "Annuel" : "Mensuel")}
            htmlFor="fixed-cost-amount"
          >
            <div className="space-y-2">
              {formValues.periodicity !== "PONCTUELLE" ? (
                <div className="flex flex-wrap gap-2">
                  {AMOUNT_MODES.map((mode) => {
                    const isSelected = amountMode === mode.key;
                    return (
                      <Button
                        key={mode.key}
                        type="button"
                        variant="outline"
                        onClick={() => handleAmountModeChange(mode.key)}
                        className={
                          isSelected
                            ? "border-lenvia-blue/40 bg-lenvia-blue/10 text-lenvia-blue hover:bg-lenvia-blue/15"
                            : "border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
                        }
                      >
                        {mode.label}
                      </Button>
                    );
                  })}
                </div>
              ) : null}
              <Input
                id="fixed-cost-amount"
                type="number"
                min="0"
                step="0.01"
                value={formValues.amount}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    amount: event.target.value,
                  }))
                }
                required
              />
              {Number.isFinite(Number(formValues.amount)) && formValues.periodicity !== "PONCTUELLE" ? (
                <p className="text-xs text-slate-400">
                  {amountMode === "ANNUAL"
                    ? `Stockage mensuel: ${formatEUR0(toMonthlyAmount(Number(formValues.amount), "ANNUAL"))}`
                    : `Équivalent annuel: ${formatEUR0(toAnnualAmount(Number(formValues.amount), "MONTHLY"))}`}
                </p>
              ) : null}
            </div>
          </PilotageField>

          {formValues.periodicity !== "PONCTUELLE" ? (
            <>
              <PilotageField label="Début (mois)" htmlFor="fixed-cost-start-month">
                <Input
                  id="fixed-cost-start-month"
                  type="month"
                  value={formValues.start_month}
                  onChange={(event) =>
                    setFormValues((current) => ({ ...current, start_month: event.target.value }))
                  }
                />
              </PilotageField>

              <PilotageField label="Fin (mois)" htmlFor="fixed-cost-end-month">
                <Input
                  id="fixed-cost-end-month"
                  type="month"
                  value={formValues.end_month}
                  onChange={(event) =>
                    setFormValues((current) => ({ ...current, end_month: event.target.value }))
                  }
                />
              </PilotageField>
            </>
          ) : null}

          <PilotageField label="Actif" htmlFor="fixed-cost-active">
            <label
              htmlFor="fixed-cost-active"
              className="flex h-9 items-center gap-2 rounded-md border border-white/10 bg-slate-950 px-3 text-sm text-slate-200"
            >
              <input
                id="fixed-cost-active"
                type="checkbox"
                checked={formValues.is_active}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    is_active: event.target.checked,
                  }))
                }
              />
              Charge active
            </label>
          </PilotageField>

          <div className="md:col-span-2">
            <PilotageField label="Notes" htmlFor="fixed-cost-notes">
              <Textarea
                id="fixed-cost-notes"
                value={formValues.notes}
                onChange={(event) =>
                  setFormValues((current) => ({ ...current, notes: event.target.value }))
                }
                placeholder="Contexte interne, précisions..."
                className="min-h-[96px] border-white/10 bg-slate-950 text-white"
              />
            </PilotageField>
          </div>
        </div>

        {formError ? (
          <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {formError}
          </p>
        ) : null}
      </PilotageModal>
    </div>
  );
};

export default FixedCostsPage;
